import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Trash2,
  Edit3,
  Send,
  Copy,
  Plus,
  Search,
  Heart,
  MessageCircle,
  MoreHorizontal,
  CheckSquare,
  Square,
  X,
  ExternalLink,
  Sparkles,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  Music2,
  RotateCcw,
  RefreshCw,
  Undo2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { postsAPI, getErrorMessage } from '../../services/api';

interface PostItem {
  id: string;
  title?: string;
  text: string;
  status: string;
  target_accounts: string[];
  target_platforms: string[];
  scheduled_at: string | null;
  published_at: string | null;
  media_count: number;
  media_thumbnail: string | null;
  total_likes: number;
  total_comments: number;
  total_shares?: number;
  created_at: string;
  updated_at?: string;
  hashtags?: string[];
  publish_results?: Record<string, { status: string; platform?: string; url?: string; error?: string; confirmed_at?: string; rolled_back_at?: string }>;
}

const getStatusConfig = (t: (key: string, defaultValue: string) => string): Record<
  string,
  { label: string; color: string; bg: string; dot: string; icon: React.ElementType }
> => ({
  draft: { label: t('posts_status_draft', 'Draft'), color: 'text-n-text-md', bg: 'bg-n-hover', dot: 'bg-n-text-lt', icon: FileText },
  scheduled: { label: t('posts_status_scheduled', 'Scheduled'), color: 'text-n-blue', bg: 'bg-n-blue-bg', dot: 'bg-n-blue', icon: Clock },
  publishing: { label: t('posts_status_publishing', 'Publishing'), color: 'text-n-amber', bg: 'bg-n-amber-bg', dot: 'bg-n-amber', icon: Send },
  published: { label: t('posts_status_published', 'Published'), color: 'text-n-green', bg: 'bg-n-green-bg', dot: 'bg-n-green', icon: CheckCircle },
  partially_published: { label: t('posts_status_partially_published', 'Partial'), color: 'text-n-amber', bg: 'bg-n-amber-bg', dot: 'bg-n-amber', icon: AlertTriangle },
  failed: { label: t('posts_status_failed', 'Failed'), color: 'text-n-red', bg: 'bg-n-red-bg', dot: 'bg-n-red', icon: AlertCircle },
  cancelled: { label: t('posts_status_cancelled', 'Cancelled'), color: 'text-n-text-lt', bg: 'bg-n-hover', dot: 'bg-n-text-lt', icon: X },
});

const getTabs = (t: (key: string, defaultValue: string) => string) => [
  { key: '', label: t('posts_tab_all', 'All Posts') },
  { key: 'draft', label: t('posts_tab_drafts', 'Drafts') },
  { key: 'scheduled', label: t('posts_tab_scheduled', 'Scheduled') },
  { key: 'published', label: t('posts_tab_published', 'Published') },
  { key: 'failed', label: t('posts_tab_failed', 'Failed') },
];

export default function PostsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [previewPost, setPreviewPost] = useState<PostItem | null>(null);
  const limit = 10;

  // --- Data ---
  const { data, isLoading } = useQuery({
    queryKey: ['posts', activeTab, page],
    queryFn: () => postsAPI.list({ status: activeTab || undefined, page, limit }).then((r) => r.data),
    staleTime: 15_000,
  });

  const posts: PostItem[] = useMemo(() => data?.posts || [], [data?.posts]);
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  const filteredPosts = useMemo(() => {
    if (!searchQuery) { return posts; }
    const q = searchQuery.toLowerCase();
    return posts.filter((p) => p.text.toLowerCase().includes(q));
  }, [posts, searchQuery]);

  // --- Counts per tab (dedicated aggregation endpoint) ---
  const { data: allData } = useQuery({
    queryKey: ['posts-counts'],
    queryFn: async () => {
      const res = await postsAPI.counts();
      return res.data as Record<string, number>;
    },
    staleTime: 30_000,
  });

  // --- Mutations ---
  const deleteMutation = useMutation({
    mutationFn: (id: string) => postsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts-counts'] });
      toast.success('Post deleted');
      setOpenMenuId(null);
    },
    onError: () => toast.error('Failed to delete'),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => postsAPI.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts-counts'] });
      toast.success('Post duplicated as draft');
      setOpenMenuId(null);
    },
    onError: () => toast.error('Failed to duplicate'),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => postsAPI.publishNow(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts-counts'] });
      const results = (response?.data as { results?: Record<string, { status: string; platform?: string; error?: string }> })?.results;
      const failures = results
        ? Object.values(results).filter((r) => r.status === 'failed')
        : [];
      if (failures.length > 0) {
        const failedNames = failures.map((f) => `${f.platform || 'unknown'}: ${f.error || 'failed'}`).join(', ');
        toast.error(`Some platforms failed: ${failedNames}`);
      } else {
        toast.success('Published successfully');
      }
      setOpenMenuId(null);
    },
    onError: (error: unknown) => toast.error(getErrorMessage(error, 'Failed to publish')),
  });

  const toDraftMutation = useMutation({
    mutationFn: (id: string) => postsAPI.toDraft(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['posts-counts'] });
      toast.success(t('posts_moved_to_draft', 'Post moved to draft'));
      setOpenMenuId(null);
    },
    onError: (error: unknown) => toast.error(getErrorMessage(error, 'Failed to move to draft')),
  });

  // Ph4 — Re-queue a failed/partially-published post. Backend clears
  // only the failed per-account entries (successes stay stamped), so
  // this never double-publishes to a platform that already accepted.
  const retryMutation = useMutation({
    mutationFn: (id: string) => postsAPI.retry(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      toast.success(t('posts_retry_enqueued', 'Retry enqueued'));
      setOpenMenuId(null);
    },
    onError: (error: unknown) =>
      toast.error(getErrorMessage(error, t('posts_retry_failed', 'Failed to retry'))),
  });

  // Ph6 — Delete a published post from every platform it was pushed
  // to. Best-effort per platform; TikTok returns supported=false
  // because the API doesn't expose a delete endpoint. We confirm
  // via window.confirm because the action is irreversible.
  const rollbackMutation = useMutation({
    mutationFn: (id: string) => postsAPI.rollback(id),
    onSuccess: (res) => {
      const results = (res.data as { results?: Record<string, { success: boolean; supported: boolean; platform?: string; error?: string }> })?.results || {};
      const succeeded = Object.values(results).filter((r) => r.success).length;
      const unsupported = Object.values(results).filter((r) => !r.supported).length;
      const failed = Object.values(results).filter((r) => r.supported && !r.success).length;
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      if (failed === 0 && unsupported === 0) {
        toast.success(
          t('posts_rollback_all_ok', 'Deleted from {{n}} platform(s)', {
            n: succeeded,
          })
        );
      } else if (failed === 0) {
        toast.success(
          t(
            'posts_rollback_partial_unsupported',
            '{{ok}} deleted, {{u}} unsupported',
            { ok: succeeded, u: unsupported }
          )
        );
      } else {
        toast.error(
          t('posts_rollback_partial_failed', '{{ok}} deleted, {{f}} failed', {
            ok: succeeded,
            f: failed,
          })
        );
      }
      setOpenMenuId(null);
    },
    onError: (error: unknown) =>
      toast.error(
        getErrorMessage(error, t('posts_rollback_failed', 'Rollback failed'))
      ),
  });


  // --- Selection helpers ---
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredPosts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredPosts.map((p) => p.id)));
    }
  };

  // --- Helpers ---
  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
  };

  const formatFullDate = (iso: string | null) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  const allSelected = filteredPosts.length > 0 && selectedIds.size === filteredPosts.length;
  const STATUS_CONFIG = useMemo(() => getStatusConfig(t), [t]);
  const TABS = useMemo(() => getTabs(t), [t]);

  // Content type emoji mapper
  const contentTypeEmoji: Record<string, string> = {
    post: '📝',
    story: '🎬',
    reel: '🎞️',
    carousel: '🖼️',
    igtv: '📺',
    short: '⚡',
  };

  // Get content type from post text (simplified - in reality would come from post model)
  const getContentEmoji = () => {
    // This is a placeholder - in production, content_type would be a field on the post
    return contentTypeEmoji.post;
  };

  // Platform colors for stacked avatars
  const platformColors: Record<string, string> = {
    instagram: 'bg-gradient-to-br from-purple-400 to-pink-600',
    facebook: 'bg-blue-600',
    twitter: 'bg-black',
    tiktok: 'bg-black',
    linkedin: 'bg-blue-700',
    youtube: 'bg-red-600',
    pinterest: 'bg-red-700',
    bluesky: 'bg-blue-500',
    threads: 'bg-black',
  };

  // Platform icon components (lucide-react brand icons where available)
  const platformIcons: Record<string, React.ElementType> = {
    instagram: Instagram,
    facebook: Facebook,
    twitter: Twitter,
    x: Twitter,
    tiktok: Music2,
    linkedin: Linkedin,
    youtube: Youtube,
  };

  // Platform name for tooltip / aria
  const platformNames: Record<string, string> = {
    instagram: 'Instagram',
    facebook: 'Facebook',
    twitter: 'X',
    x: 'X',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
    pinterest: 'Pinterest',
    bluesky: 'Bluesky',
    threads: 'Threads',
  };

  // Text fallback for platforms without a lucide icon
  const platformAbbrev: Record<string, string> = {
    pinterest: 'P',
    bluesky: 'B',
    threads: 'Th',
  };

  const getPlatformColor = (platform: string) => {
    const normalized = platform.toLowerCase();
    return platformColors[normalized] || 'bg-n-text-lt';
  };

  const getPlatformName = (platform: string) => {
    const normalized = platform.toLowerCase();
    return platformNames[normalized] || platform;
  };

  const renderPlatformIcon = (platform: string) => {
    const normalized = platform.toLowerCase();
    const Icon = platformIcons[normalized];
    if (Icon) {
      return <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />;
    }
    return (
      <span className="text-[10px] font-bold leading-none">
        {platformAbbrev[normalized] || platform.slice(0, 2).toUpperCase()}
      </span>
    );
  };

  return (
    <div className="h-full flex flex-col bg-n-card overflow-hidden">
      {/* Page Header — fixed */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-start gap-3 mb-1">
          <span className="text-4xl leading-none mt-0.5">📮</span>
          <h1 className="text-4xl font-bold text-n-text">Posts</h1>
        </div>
        <p className="text-sm text-n-text-md ml-12">Every post, story, reel and carousel across all your channels.</p>
      </div>

      {/* Database View */}
      <div className="flex-1 min-h-0 flex flex-col border-t border-n-border">
          {/* Views + Toolbar — fixed */}
          <div className="flex-shrink-0 flex items-center justify-between gap-4 px-6 pt-4 pb-4 border-b border-n-border bg-n-card z-10">
            <div className="flex items-center gap-1">
              {TABS.map((tab) => {
                const count = tab.key === '' ? (allData?.all ?? total) : (allData?.[tab.key] ?? 0);
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => {
                      setActiveTab(tab.key);
                      setPage(1);
                      setSelectedIds(new Set());
                    }}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border-b-2 transition-colors ${
                      isActive
                        ? 'text-n-text border-n-text'
                        : 'text-n-text-md border-transparent hover:text-n-text'
                    }`}
                  >
                    {tab.key !== '' && (
                      <span
                        className="w-1.5 h-1.5 rounded-full"
                        style={{
                          backgroundColor:
                            tab.key === 'draft'
                              ? '#9b9a97'
                              : tab.key === 'scheduled'
                              ? '#337ea9'
                              : tab.key === 'publishing'
                              ? '#cb912f'
                              : tab.key === 'published'
                              ? '#448361'
                              : tab.key === 'failed'
                              ? '#d44c47'
                              : '#9b9a97',
                        }}
                      />
                    )}
                    {tab.label}
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-n-hover text-n-text-md">
                      {count}
                    </span>
                  </button>
                );
              })}
              <button
                className="p-1 text-n-text-lt hover:text-n-text-md rounded transition-colors ml-2"
                title={t('posts_notion_add_view', 'Add view')}
              >
                <Plus size={14} />
              </button>
            </div>

            {/* Right Toolbar */}
            <div className="flex items-center gap-2">
              <div className="relative flex-shrink-0 w-40">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-n-text-lt" />
                <input
                  type="text"
                  placeholder={t('posts_notion_search', 'Search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-7 pr-2 py-1.5 text-xs bg-n-page border border-n-border rounded hover:border-n-text-lt focus:outline-none focus:ring-1 focus:ring-n-blue placeholder:text-n-text-lt"
                />
              </div>
              <button
                onClick={() => navigate('/compose')}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-white rounded transition-colors"
                style={{ backgroundColor: '#2383e2' }}
                title={t('posts_new_post', 'New')}
              >
                <Plus size={12} />
                New
              </button>
            </div>
          </div>

          {/* Active Filters Row */}
          {(activeTab || searchQuery) && (
            <div className="flex-shrink-0 flex items-center gap-2 flex-wrap px-6 py-2 border-b border-n-border text-xs">
              {activeTab && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-n-hover text-n-text-md">
                  {t('posts_notion_filter_status', 'Status')}{' '}
                  <span className="text-n-text-lt font-normal">{t('posts_notion_filter_is', 'is')}</span>{' '}
                  <span className="font-semibold">
                    {STATUS_CONFIG[activeTab]?.label ?? activeTab}
                  </span>
                  <button
                    onClick={() => { setActiveTab(''); setPage(1); }}
                    className="ml-1 text-n-text-lt hover:text-n-text"
                    aria-label={t('posts_notion_clear_filter', 'Clear filter')}
                  >×</button>
                </span>
              )}
              {searchQuery && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-n-hover text-n-text-md">
                  {t('posts_notion_filter_search', 'Search')}:{' '}
                  <span className="font-semibold">&ldquo;{searchQuery}&rdquo;</span>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="ml-1 text-n-text-lt hover:text-n-text"
                    aria-label={t('posts_notion_clear_filter', 'Clear filter')}
                  >×</button>
                </span>
              )}
            </div>
          )}

          {/* Table — the only scrolling area */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6">
          {isLoading ? (
            <div className="divide-y divide-n-border">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-3 animate-pulse">
                  <div className="w-4 h-4 bg-n-border rounded" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-n-border rounded w-2/3" />
                    <div className="h-2.5 bg-n-hover rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-10">
              <FileText size={32} className="mx-auto text-n-text-lt mb-3" />
              <h3 className="font-semibold text-n-text-md mb-1">
                {searchQuery ? t('posts_no_matching', 'No matching posts') : activeTab ? t('posts_no_status_posts', `No ${activeTab} posts`) : t('posts_no_posts_yet', 'No posts yet')}
              </h3>
              <p className="text-xs text-n-text-lt">
                {searchQuery
                  ? t('posts_adjust_search', 'Try adjusting your search or create a new post.')
                  : t('posts_start_creating', 'Start creating content and manage posts from here.')}
              </p>
            </div>
          ) : (
            <>
              <table className="w-full table-fixed border-collapse text-sm">
                <colgroup>
                  <col style={{ width: 36 }} />
                  <col />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 128 }} />
                  <col style={{ width: 64 }} />
                  <col style={{ width: 130 }} />
                  <col style={{ width: 112 }} />
                </colgroup>
                <thead>
                  <tr className="border-b border-n-border">
                    <th className="px-3 py-2 text-left text-xs font-medium text-n-text-lt font-normal">
                      <button onClick={toggleSelectAll} className="text-n-text-lt hover:text-n-text">
                        {allSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                      </button>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-n-text-lt font-normal whitespace-nowrap">
                      <span className="inline-flex items-center gap-1"><span className="text-[10px] leading-none">𝐀𝐚</span>Content</span>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-n-text-lt font-normal whitespace-nowrap">
                      <span className="inline-flex items-center gap-1"><span className="text-[10px] leading-none">◉</span>Status</span>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-n-text-lt font-normal whitespace-nowrap">
                      <span className="inline-flex items-center gap-1"><span className="text-[10px] leading-none">◈</span>Platforms</span>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-n-text-lt font-normal whitespace-nowrap">
                      <span className="inline-flex items-center gap-1"><span className="text-[10px] leading-none">◱</span>Media</span>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-n-text-lt font-normal whitespace-nowrap">
                      <span className="inline-flex items-center gap-1"><span className="text-[10px] leading-none">◷</span>Updated</span>
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-n-text-lt font-normal whitespace-nowrap hidden lg:table-cell">
                      <span className="inline-flex items-center gap-1"><span className="text-[10px] leading-none">△</span>Engagement</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map((post) => {
                    const sc = STATUS_CONFIG[post.status] || STATUS_CONFIG.draft;
                    const isSelected = selectedIds.has(post.id);

                    return (
                      <tr
                        key={post.id}
                        className={`border-b border-n-border transition-colors group hover:bg-n-hover ${
                          isSelected ? 'bg-n-blue-bg' : ''
                        }`}
                      >
                        {/* Checkbox */}
                        <td className="px-3 py-2">
                          <button onClick={() => toggleSelect(post.id)} className="text-n-text-lt hover:text-n-text">
                            {isSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                          </button>
                        </td>

                        {/* Content */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="flex-shrink-0 w-5 h-5 rounded-sm bg-n-hover flex items-center justify-center text-xs">
                              {getContentEmoji()}
                            </span>
                            <button
                              onClick={() => navigate(`/compose/${post.id}`)}
                              className="flex-1 min-w-0 text-left"
                            >
                              <span className="text-n-text font-medium truncate hover:underline">
                                {post.title || post.text || <span className="text-n-text-lt italic">No content</span>}
                              </span>
                            </button>
                            <div className="flex-shrink-0 gap-1 flex opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => { e.stopPropagation(); setPreviewPost(previewPost?.id === post.id ? null : post); }}
                                className="p-1 text-n-text-lt hover:text-n-text-md hover:bg-n-card rounded border border-n-border"
                                title={t('posts_notion_open', 'Preview')}
                              >
                                <ExternalLink size={10} />
                              </button>
                              <button
                                onClick={() => setOpenMenuId(openMenuId === post.id ? null : post.id)}
                                className="p-1 text-n-text-lt hover:text-n-text-md hover:bg-n-card rounded border border-n-border"
                                title={t('posts_notion_more', 'More')}
                              >
                                <MoreHorizontal size={10} />
                              </button>
                            </div>
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-3 py-2">
                          <span
                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-semibold"
                            style={{
                              backgroundColor:
                                post.status === 'draft'
                                  ? '#e3e2e0'
                                  : post.status === 'scheduled'
                                  ? '#d3e5ef'
                                  : post.status === 'publishing'
                                  ? '#fdecc8'
                                  : post.status === 'published'
                                  ? '#dbeddb'
                                  : post.status === 'partially_published'
                                  ? '#fdecc8'
                                  : post.status === 'failed'
                                  ? '#ffe2dd'
                                  : '#e3e2e0',
                              color:
                                post.status === 'draft'
                                  ? '#32302c'
                                  : post.status === 'scheduled'
                                  ? '#183347'
                                  : post.status === 'publishing'
                                  ? '#402c1b'
                                  : post.status === 'published'
                                  ? '#1c3829'
                                  : post.status === 'partially_published'
                                  ? '#402c1b'
                                  : post.status === 'failed'
                                  ? '#5d1715'
                                  : '#32302c',
                            }}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{
                                backgroundColor:
                                  post.status === 'draft'
                                    ? '#9b9a97'
                                    : post.status === 'scheduled'
                                    ? '#337ea9'
                                    : post.status === 'publishing'
                                    ? '#cb912f'
                                    : post.status === 'published'
                                    ? '#448361'
                                    : post.status === 'partially_published'
                                    ? '#cb912f'
                                    : post.status === 'failed'
                                    ? '#d44c47'
                                    : '#9b9a97',
                              }}
                            />
                            {sc.label}
                          </span>
                        </td>

                        {/* Platforms */}
                        <td className="px-3 py-2">
                          {(post.target_platforms || []).length > 0 ? (
                            <div className="flex items-center -space-x-1.5">
                              {post.target_platforms.slice(0, 4).map((platform, i) => {
                                // Find per-platform publish result if available
                                const pr = post.publish_results || {};
                                const platformResult = Object.values(pr).find(
                                  (r) => r?.platform === platform
                                );
                                const hasFailed = platformResult?.status === 'failed' || platformResult?.status === 'error';
                                const hasSucceeded = platformResult?.status === 'success' || platformResult?.status === 'published';
                                const isConfirmed = Boolean(platformResult?.confirmed_at);
                                const isRolledBack = Boolean(platformResult?.rolled_back_at);
                                const errorMsg = platformResult?.error;

                                return (
                                  <div
                                    key={i}
                                    className="relative"
                                    title={
                                      isRolledBack
                                        ? `${getPlatformName(platform)}: Deleted from platform`
                                        : isConfirmed
                                        ? `${getPlatformName(platform)}: Confirmed by webhook`
                                        : hasFailed
                                        ? `${getPlatformName(platform)}: Failed${errorMsg ? ` — ${errorMsg}` : ''}`
                                        : hasSucceeded
                                        ? `${getPlatformName(platform)}: Published`
                                        : getPlatformName(platform)
                                    }
                                  >
                                    <div
                                      className={`w-6 h-6 rounded-full text-white flex items-center justify-center ring-2 ring-white ${getPlatformColor(platform)} ${hasFailed || isRolledBack ? 'opacity-50' : ''}`}
                                      aria-label={getPlatformName(platform)}
                                    >
                                      {renderPlatformIcon(platform)}
                                    </div>
                                    {/* Precedence: rolled_back > confirmed > failed > succeeded.
                                        Confirmed wins over succeeded because webhook-confirmed
                                        means the platform ACK'd the publish end-to-end. */}
                                    {isRolledBack && (
                                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-slate-500 text-white flex items-center justify-center text-[7px] font-bold ring-1 ring-white" title="Deleted">↩</span>
                                    )}
                                    {!isRolledBack && isConfirmed && (
                                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-600 text-white flex items-center justify-center text-[7px] font-bold ring-1 ring-white" title="Confirmed">✓</span>
                                    )}
                                    {!isRolledBack && !isConfirmed && hasFailed && (
                                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-red-500 text-white flex items-center justify-center text-[7px] font-bold ring-1 ring-white">✕</span>
                                    )}
                                    {!isRolledBack && !isConfirmed && !hasFailed && hasSucceeded && (
                                      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 text-white flex items-center justify-center text-[7px] font-bold ring-1 ring-white">✓</span>
                                    )}
                                  </div>
                                );
                              })}
                              {post.target_platforms.length > 4 && (
                                <div className="w-6 h-6 rounded-full bg-n-hover text-n-text-md text-[10px] font-semibold flex items-center justify-center ring-2 ring-white">
                                  +{post.target_platforms.length - 4}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-n-text-lt">—</span>
                          )}
                        </td>

                        {/* Media */}
                        <td className="px-3 py-2 text-center">
                          {post.media_count > 0 ? (
                            <span className="text-n-text-md text-xs font-medium">{post.media_count}</span>
                          ) : (
                            <span className="text-n-text-lt">—</span>
                          )}
                        </td>

                        {/* Date */}
                        <td className="px-3 py-2">
                          <span className="text-xs text-n-text-md whitespace-nowrap">
                            {formatDate(post.updated_at || post.created_at)}
                          </span>
                          {post.status === 'scheduled' && post.scheduled_at && (
                            <span className="block text-[10px] text-n-text-lt whitespace-nowrap mt-0.5">
                              {t('posts_scheduled_for', { date: formatFullDate(post.scheduled_at) })}
                            </span>
                          )}
                        </td>

                        {/* Engagement */}
                        <td className="px-3 py-2 hidden lg:table-cell">
                          {post.status === 'published' ? (
                            <div className="flex items-center gap-3">
                              <span className="flex items-center gap-1 text-xs text-n-text-md">
                                <Heart size={11} className="text-n-red fill-n-red" />
                                {post.total_likes}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-n-text-md">
                                <MessageCircle size={11} className="text-n-blue fill-n-blue" />
                                {post.total_comments}
                              </span>
                            </div>
                          ) : (
                            <span className="text-n-text-lt">—</span>
                          )}
                        </td>

                        {/* Context Menu */}
                        {openMenuId === post.id && (
                          <>
                            <td colSpan={7} className="p-0">
                              <div className="fixed inset-0 z-10" role="presentation" onClick={() => setOpenMenuId(null)} />
                              <div className="absolute right-4 top-auto z-20 w-44 bg-n-card border border-n-border rounded-n-sm py-1 animate-in fade-in shadow-n">
                                <button
                                  onClick={() => {
                                    navigate(`/compose/${post.id}`);
                                    setOpenMenuId(null);
                                  }}
                                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover"
                                >
                                  <Edit3 size={14} /> Edit
                                </button>
                                <button
                                  onClick={() => {
                                    navigate(`/studio?post=${post.id}`);
                                    setOpenMenuId(null);
                                  }}
                                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover"
                                >
                                  <Sparkles size={14} /> {t('posts_edit_in_studio', 'Edit in AI Studio')}
                                </button>
                                <button
                                  onClick={() => {
                                    duplicateMutation.mutate(post.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover"
                                >
                                  <Copy size={14} /> Duplicate
                                </button>
                                {(post.status === 'draft' || post.status === 'scheduled') && (
                                  <button
                                    onClick={() => {
                                      publishMutation.mutate(post.id);
                                      setOpenMenuId(null);
                                    }}
                                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover"
                                  >
                                    <Send size={14} /> {t('posts_publish_now', 'Publish Now')}
                                  </button>
                                )}
                                {post.status !== 'draft' && (
                                  <button
                                    onClick={() => {
                                      toDraftMutation.mutate(post.id);
                                      setOpenMenuId(null);
                                    }}
                                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover"
                                  >
                                    <RotateCcw size={14} /> {t('posts_to_drafts', 'To Drafts')}
                                  </button>
                                )}
                                {/* Ph4 — Retry is only meaningful when
                                    the post has failed / partially
                                    published entries we can re-queue. */}
                                {(post.status === 'failed' ||
                                  post.status === 'partially_published') && (
                                  <button
                                    onClick={() => {
                                      retryMutation.mutate(post.id);
                                    }}
                                    disabled={retryMutation.isPending}
                                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover"
                                  >
                                    <RefreshCw size={14} />{' '}
                                    {t('posts_retry_publish', 'Retry publish')}
                                  </button>
                                )}
                                {/* Ph6 — Best-effort delete from every
                                    platform the post landed on. The
                                    backend handles per-platform
                                    semantics; we just gate the UI. */}
                                {(post.status === 'published' ||
                                  post.status === 'partially_published') && (
                                  <button
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          t(
                                            'posts_rollback_confirm',
                                            'Delete this post from every platform it was published to? This cannot be undone.'
                                          )
                                        )
                                      ) {
                                        rollbackMutation.mutate(post.id);
                                      }
                                    }}
                                    disabled={rollbackMutation.isPending}
                                    className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-text-md hover:bg-n-hover"
                                  >
                                    <Undo2 size={14} />{' '}
                                    {t('posts_rollback_publish', 'Delete from platforms')}
                                  </button>
                                )}
                                <div className="h-px bg-n-border my-1" />
                                <button
                                  onClick={() => {
                                    deleteMutation.mutate(post.id);
                                    setOpenMenuId(null);
                                  }}
                                  className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-n-red hover:bg-n-red-bg"
                                >
                                  <Trash2 size={14} /> Delete
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}

                  {/* Add new row */}
                  <tr className="border-b border-n-border hover:bg-n-hover">
                    <td colSpan={7} className="px-3 py-3">
                      <button
                        onClick={() => navigate('/compose')}
                        className="flex items-center gap-1.5 text-n-text-lt hover:text-n-text text-xs font-medium"
                      >
                        <Plus size={12} />
                        New post
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Count row */}
              <div className="px-3 py-2 border-t border-n-border text-xs text-n-text-md">
                <span>Count</span> <span className="font-semibold text-n-text ml-2">{filteredPosts.length}</span>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-3 py-4 flex items-center justify-between text-xs border-t border-n-border">
                  <span className="text-n-text-md">
                    Showing <span className="font-semibold text-n-text">{(page - 1) * limit + 1}–{Math.min(page * limit, total)}</span> of{' '}
                    <span className="font-semibold text-n-text">{total}</span>
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="w-6 h-6 rounded flex items-center justify-center text-n-text-md hover:bg-n-hover disabled:opacity-30"
                    >
                      ‹
                    </button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`w-6 h-6 rounded flex items-center justify-center text-xs font-medium transition-colors ${
                            page === pageNum
                              ? 'bg-n-text text-white'
                              : 'text-n-text-md hover:bg-n-hover'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="w-6 h-6 rounded flex items-center justify-center text-n-text-md hover:bg-n-hover disabled:opacity-30"
                    >
                      ›
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          </div>
      </div>
    </div>
  );
}
